/**
 * Morgen mask intro — site-wide preloader.
 * Idle shake + parallax → click → fly into mouth → reveal site.
 * After enter: skip on in-tab navigations (incl. RU↔EN); replay on full refresh.
 */
(() => {
  const STORAGE_KEY = 'morgen-mask-entered';
  const SHAKE_END = 18;
  const FLY_START = 0;
  const FLY_END = 116;
  const FLY_MS = 2460;
  const STAGE_BG = '#050506';

  const root = document.querySelector('[data-mask-intro]');
  if (!root || root.dataset.ready === 'true') return;
  root.dataset.ready = 'true';

  const designMode = Boolean(window.Shopify?.designMode);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const stage = root.querySelector('[data-mask-intro-stage]');
  const canvas = root.querySelector('[data-mask-intro-canvas]');
  if (!stage || !canvas) return;

  const getNavType = () => {
    try {
      const entry = performance.getEntriesByType?.('navigation')?.[0];
      if (entry?.type) return entry.type;
      if (performance.navigation) {
        const t = performance.navigation.type;
        if (t === 1) return 'reload';
        if (t === 2) return 'back_forward';
      }
    } catch {
      /* ignore */
    }
    return 'navigate';
  };

  const readSkip = () => {
    if (designMode) return false;
    // Full refresh always replays the mask.
    if (getNavType() === 'reload') return false;
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  };

  const writeSkip = () => {
    if (designMode) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* private mode / blocked storage */
    }
  };

  const markSkippedDom = () => {
    document.documentElement.classList.add('mask-intro-skip');
    document.documentElement.classList.remove('mask-intro-pending', 'mask-idle-lock', 'mask-await-enter', 'mask-enter-dissolve');
    root.classList.add('is-complete', 'is-skipped');
    root.setAttribute('hidden', '');
    stage.style.pointerEvents = 'none';
  };

  if (readSkip() || document.documentElement.classList.contains('mask-intro-skip')) {
    markSkippedDom();
    return;
  }

  const desktopEl = root.querySelector('[data-mask-frames-desktop]');
  const mobileEl = root.querySelector('[data-mask-frames-mobile]');
  let desktop = [];
  let mobile = [];
  try {
    desktop = JSON.parse(desktopEl?.textContent || '[]');
    mobile = JSON.parse(mobileEl?.textContent || '[]');
  } catch {
    desktop = [];
    mobile = [];
  }

  if (!desktop.length && !mobile.length) {
    writeSkip();
    markSkippedDom();
    document.documentElement.classList.remove('mask-intro-pending');
    return;
  }

  const ctx = canvas.getContext('2d', { alpha: false });
  let frames = [];
  let progress = 0;
  let flyFrame = 0;
  let rendered = -1;
  let pending = false;
  let mode = 'idle';
  let shakeIndex = 0;
  let shakeDir = 1;
  let shakeLast = 0;
  let autoFade = 0;
  let enterSignaled = false;

  const clamp = (n) => Math.max(0, Math.min(1, n));
  const ease = (n) => {
    if (n <= 0) return 0;
    if (n >= 1) return 1;
    if (n < 0.38) {
      const t = n / 0.38;
      return 0.22 * t * t;
    }
    if (n < 0.82) {
      const t = (n - 0.38) / 0.44;
      return 0.22 + 0.68 * (t * t * (3 - 2 * t));
    }
    const t = (n - 0.82) / 0.18;
    return 0.9 + 0.1 * (1 - Math.pow(1 - t, 1.35));
  };

  const fit = () => {
    const d = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(canvas.clientWidth * d);
    const h = Math.round(canvas.clientHeight * d);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      rendered = -1;
    }
  };

  const available = (desired) => {
    for (let d = 0; d < frames.length; d += 1) {
      for (const i of [desired - d, desired + d]) {
        if (i >= 0 && i < frames.length && frames[i]?.complete && frames[i].naturalWidth) return i;
      }
    }
    return 0;
  };

  const draw = (index) => {
    const image = frames[index];
    if (!image?.complete || !image.naturalWidth) return;
    const cr = canvas.width / canvas.height;
    const ir = image.naturalWidth / image.naturalHeight;
    let w;
    let h;
    let x;
    let y;
    if (ir > cr) {
      h = canvas.height;
      w = h * ir;
      x = (canvas.width - w) / 2;
      y = 0;
    } else {
      w = canvas.width;
      h = w / ir;
      x = 0;
      y = (canvas.height - h) / 2;
    }
    ctx.fillStyle = STAGE_BG;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, x, y, w, h);
    rendered = index;
  };

  const setLock = (on) => {
    document.documentElement.classList.toggle('mask-idle-lock', on);
    document.documentElement.classList.toggle('mask-intro-pending', on);
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) {
      if (on) {
        wrapper.dataset.prevOverflow = wrapper.style.overflow || '';
        wrapper.style.overflow = 'hidden';
        if (window.__morgenLenis) window.__morgenLenis.stop();
      } else {
        wrapper.style.overflow = wrapper.dataset.prevOverflow || '';
        if (window.__morgenLenis) window.__morgenLenis.start();
      }
    }
  };

  const publish = () => {
    const stageOpacity = (1 - autoFade).toFixed(3);
    root.style.setProperty('--mask-canvas-opacity', stageOpacity);
    root.style.setProperty('--mask-stage-opacity', stageOpacity);
    stage.style.opacity = stageOpacity;
    root.style.setProperty('--mask-cue', mode === 'idle' ? '1' : (1 - clamp(progress / 0.12)).toFixed(3));
    root.classList.toggle('is-complete', mode === 'done');
    root.classList.toggle('is-idle', mode === 'idle');
    root.classList.toggle('is-flying', mode === 'flying');
    if (mode === 'idle') {
      document.documentElement.classList.add('mask-await-enter');
      document.documentElement.classList.remove('mask-enter-dissolve');
    } else {
      document.documentElement.classList.remove('mask-await-enter');
    }
    if (mode === 'done') document.documentElement.classList.remove('mask-enter-dissolve');
    document.documentElement.style.setProperty('--mask-progress', progress.toFixed(3));
  };

  const render = () => {
    pending = false;
    fit();
    let frame;
    if (mode === 'idle') frame = shakeIndex;
    else if (mode === 'flying') frame = flyFrame;
    else frame = Math.min(FLY_END, Math.max(frames.length - 1, 0));
    if (frame !== rendered) draw(available(frame));
    publish();
  };

  const request = () => {
    if (!pending) {
      pending = true;
      requestAnimationFrame(render);
    }
  };

  const signalEnter = () => {
    if (enterSignaled) return;
    enterSignaled = true;
    document.dispatchEvent(new CustomEvent('mask-enter-complete'));
  };

  const finish = () => {
    mode = 'done';
    progress = 1;
    autoFade = 1;
    flyFrame = Math.min(FLY_END, Math.max(frames.length - 1, 0));
    root.classList.add('is-complete');
    root.classList.remove('is-idle', 'is-flying', 'is-parallax');
    writeSkip();
    setLock(false);
    document.documentElement.classList.remove('mask-intro-pending');
    document.documentElement.classList.add('mask-intro-skip');
    stage.style.opacity = '0';
    stage.style.pointerEvents = 'none';
    publish();
    request();
    signalEnter();
    window.setTimeout(() => {
      root.setAttribute('hidden', '');
    }, 600);
  };

  const resetParallax = () => {
    root.style.setProperty('--mask-parallax-x', '0px');
    root.style.setProperty('--mask-parallax-y', '0px');
    root.style.setProperty('--mask-parallax-z', '0px');
    root.style.setProperty('--mask-parallax-rx', '0deg');
    root.style.setProperty('--mask-parallax-ry', '0deg');
    root.style.setProperty('--mask-parallax-rz', '0deg');
    root.style.setProperty('--mask-parallax-s', '1.055');
  };

  const flyIn = () => {
    if (mode !== 'idle' || !frames.length) return;
    document.documentElement.classList.add('mask-enter-dissolve');
    document.documentElement.classList.remove('mask-await-enter');
    window.setTimeout(() => {
      document.documentElement.classList.remove('mask-enter-dissolve');
    }, reduced.matches ? 160 : 580);

    if (reduced.matches) {
      window.setTimeout(finish, 180);
      return;
    }

    mode = 'flying';
    root.classList.remove('is-idle');
    root.classList.add('is-flying');
    const last = Math.max(frames.length - 1, 1);
    const from = Math.min(Math.max(FLY_START, 0), last);
    const to = Math.min(Math.max(FLY_END, from + 1), last);
    const span = Math.max(to - from, 1);
    flyFrame = from;
    progress = from / last;
    autoFade = 0;
    enterSignaled = false;
    resetParallax();
    draw(available(from));
    publish();

    const started = performance.now();
    const run = (now) => {
      const t = clamp((now - started) / FLY_MS);
      const eased = ease(t);
      flyFrame = Math.round(from + span * eased);
      progress = flyFrame / last;
      for (let ahead = 1; ahead <= 3; ahead += 1) {
        const next = Math.min(to, flyFrame + ahead);
        frames[next]?.decode?.().catch(() => {});
      }
      autoFade = clamp((t - 0.78) / 0.2);
      if (t >= 0.62) signalEnter();
      request();
      if (t < 1) requestAnimationFrame(run);
      else finish();
    };
    requestAnimationFrame(run);
  };

  const setupFrames = () => {
    const sources = window.matchMedia('(max-width: 749px)').matches ? mobile : desktop;
    const list = sources.length ? sources : desktop.length ? desktop : mobile;
    frames = list.map((src) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
      img.addEventListener('load', () => request(), { once: true });
      return img;
    });
    for (let i = 0; i <= Math.min(SHAKE_END, frames.length - 1); i += 1) {
      frames[i]?.decode?.().catch(() => {});
    }
    for (let i = SHAKE_END + 1; i <= Math.min(FLY_END, frames.length - 1); i += 1) {
      frames[i]?.decode?.().catch(() => {});
    }
    rendered = -1;
    request();
  };

  const tickIdle = (now) => {
    if (mode !== 'idle') return;
    if (!reduced.matches && now - shakeLast > 56) {
      shakeLast = now;
      shakeIndex += shakeDir;
      if (shakeIndex >= SHAKE_END) {
        shakeIndex = SHAKE_END;
        shakeDir = -1;
      } else if (shakeIndex <= 0) {
        shakeIndex = 0;
        shakeDir = 1;
      }
      root.dataset.shakeFrame = String(shakeIndex);
      request();
    }
    requestAnimationFrame(tickIdle);
  };

  if (!reduced.matches) {
    root.classList.add('is-parallax');
    let tx = 0;
    let ty = 0;
    let tz = 0;
    let trx = 0;
    let try_ = 0;
    let trz = 0;
    let ts = 1.055;
    let cx = 0;
    let cy = 0;
    let cz = 0;
    let crx = 0;
    let cry = 0;
    let crz = 0;
    let cs = 1.055;
    let parallaxRaf = 0;
    const applyParallax = () => {
      cx += (tx - cx) * 0.12;
      cy += (ty - cy) * 0.12;
      cz += (tz - cz) * 0.1;
      crx += (trx - crx) * 0.12;
      cry += (try_ - cry) * 0.12;
      crz += (trz - crz) * 0.1;
      cs += (ts - cs) * 0.1;
      root.style.setProperty('--mask-parallax-x', `${cx.toFixed(2)}px`);
      root.style.setProperty('--mask-parallax-y', `${cy.toFixed(2)}px`);
      root.style.setProperty('--mask-parallax-z', `${cz.toFixed(2)}px`);
      root.style.setProperty('--mask-parallax-rx', `${crx.toFixed(3)}deg`);
      root.style.setProperty('--mask-parallax-ry', `${cry.toFixed(3)}deg`);
      root.style.setProperty('--mask-parallax-rz', `${crz.toFixed(3)}deg`);
      root.style.setProperty('--mask-parallax-s', cs.toFixed(4));
      const moving =
        Math.abs(tx - cx) > 0.05 ||
        Math.abs(ty - cy) > 0.05 ||
        Math.abs(trx - crx) > 0.01 ||
        Math.abs(try_ - cry) > 0.01;
      parallaxRaf = moving && mode === 'idle' ? requestAnimationFrame(applyParallax) : 0;
    };
    const askParallax = () => {
      if (!parallaxRaf) parallaxRaf = requestAnimationFrame(applyParallax);
    };
    window.addEventListener(
      'pointermove',
      (event) => {
        if (mode !== 'idle') return;
        const x = event.clientX / Math.max(window.innerWidth, 1) - 0.5;
        const y = event.clientY / Math.max(window.innerHeight, 1) - 0.5;
        tx = x * 26;
        ty = y * 16;
        tz = (Math.abs(x) + Math.abs(y)) * -22;
        trx = -y * 3.15;
        try_ = x * 3.85;
        trz = x * -1.35;
        ts = 1.055 + Math.min(0.04, (Math.abs(x) + Math.abs(y)) * 0.045);
        askParallax();
      },
      { passive: true }
    );
  }

  const startEnter = () => {
    if (mode !== 'idle') return;
    flyIn();
  };

  stage.addEventListener('click', startEnter);
  stage.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      startEnter();
    }
  });

  window.addEventListener(
    'pageshow',
    (event) => {
      if (event.persisted && readSkip()) {
        markSkippedDom();
        setLock(false);
      }
    },
    { passive: true }
  );

  window.addEventListener(
    'resize',
    () => {
      if (mode === 'done') return;
      setupFrames();
      request();
    },
    { passive: true }
  );

  reduced.addEventListener?.('change', () => {
    if (reduced.matches && mode === 'idle') finish();
  });

  if (reduced.matches) {
    setLock(true);
    publish();
    window.setTimeout(finish, 120);
    return;
  }

  root.classList.add('is-idle', 'is-armed');
  setLock(true);
  setupFrames();
  publish();
  requestAnimationFrame(tickIdle);
})();
